import { useNavigate, useSearchParams } from "react-router-dom";
import PageScaffold from "../../components/PageScaffold";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import AuthenticationContext from "../../contexts/auth/AuthenticationContext";
import { formatNumber } from "@leedpulse-monorepo/utils";
import { EProductType, UserRole } from "@leedpulse-monorepo/enums";
import { Alert, Button, Flex, Radio, Segmented, Typography } from "antd";
import PlansList from "../../components/plans_page/PlansList";
import LeedflowStripeProductsList from "../../components/plans_page/LeedflowStripeProductsList";
import HttpClient from "../../../core/HttpClient";
import { IStripeProducts, TStripeProductStatusFilter } from "../../../domain/models/stripe/Stripe";
import { EyeOutlined } from "@ant-design/icons";

// Images
import { LeedpulseLogoLight as leedpulseLogo } from '@leedpulse-monorepo/assets';
import { LeedflowLogoLight as leedflowLogo } from '@leedpulse-monorepo/assets';

export const PLANS_PAGE_PATH = "/plans";
export const PLANS_PAGE_TITLE = "Plans";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PlansPageProps { }

const PRODUCT_QUERYKEY = 'product';
const STRIPE_STATUS_QUERYKEY = 'stripe_status';
const ARCHIVED_QUERYKEY = 'archived';
const PLANS_PAGE_PRODUCT_LOCALSTORAGE_KEY = 'plans_page.product';
const PLANS_PAGE_ORPHAN_ALERT_LOCALSTORAGE_KEY = 'plans_page.orphan_alert_closed';

type TPlanArchivedFilter = 'all' | 'not_archived' | 'archived';

const parsePlansProductFromUrl = (value: string | null): EProductType =>
  value === EProductType.LEEDFLOW ? EProductType.LEEDFLOW : EProductType.LEEDPULSE;

const parseStripeStatusFromUrl = (value: string | null): TStripeProductStatusFilter =>
  value === 'all' || value === 'archived' ? value : 'active';

const parseArchivedFromUrl = (value: string | null): TPlanArchivedFilter =>
  value === 'all' || value === 'archived' ? value : 'not_archived';

const parsePlansProductFromLocalStorage = (value: string | null): EProductType | null => {
  if (value === EProductType.LEEDFLOW) return EProductType.LEEDFLOW;
  if (value === EProductType.LEEDPULSE) return EProductType.LEEDPULSE;
  return null;
};

const PlansPage: React.FC<PlansPageProps> = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = useContext(AuthenticationContext);

  const productQueryParam = searchParams.get(PRODUCT_QUERYKEY);
  const plansProduct = useMemo(() => parsePlansProductFromUrl(productQueryParam), [productQueryParam]);
  const stripeProductFilter = parseStripeStatusFromUrl(searchParams.get(STRIPE_STATUS_QUERYKEY));
  const planArchivedFilter = parseArchivedFromUrl(searchParams.get(ARCHIVED_QUERYKEY));

  const [plans, setPlans] = useState<Record<string, any>[]>([]);
  const [page, setPage] = useState<number>(0);
  const [nbHits, setNbHits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useState('');
  const [hitsPerPage, setHitsPerPage] = useState<number>(20);
  const [stripeProducts, setStripeProducts] = useState<IStripeProducts | null>(null);
  const [isOrphanAlertClosed, setIsOrphanAlertClosed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(PLANS_PAGE_ORPHAN_ALERT_LOCALSTORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const didResolveInitialProductRef = useRef(false);
  const isProductInitializedRef = useRef(false);
  const pendingInitialProductRef = useRef<EProductType | null>(null);

  const setProductTab = (nextProduct: EProductType) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextProduct === EProductType.LEEDPULSE) {
          next.delete(STRIPE_STATUS_QUERYKEY);
          next.delete(PRODUCT_QUERYKEY);
        } else {
          next.set(PRODUCT_QUERYKEY, EProductType.LEEDFLOW);
        }
        return next;
      },
      { replace: true }
    );
  };

  const setStripeStatusInUrl = (status: TStripeProductStatusFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(PRODUCT_QUERYKEY, EProductType.LEEDFLOW);
        next.set(STRIPE_STATUS_QUERYKEY, status);
        return next;
      },
      { replace: true }
    );
  };

  const setArchivedFilterInUrl = (filter: TPlanArchivedFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(ARCHIVED_QUERYKEY, filter);
        return next;
      },
      { replace: true }
    );
  };

  const closeOrphanAlert = (closed: boolean) => {
    setIsOrphanAlertClosed(closed);
    try {
      window.localStorage.setItem(PLANS_PAGE_ORPHAN_ALERT_LOCALSTORAGE_KEY, String(closed));
    } catch {
      // ignore
    }
  };

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );

  async function searchPlans(pageToFetch = 0, newHitsPerPage = hitsPerPage) {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        q: query,
        page: String(pageToFetch),
        hitsPerPage: String(newHitsPerPage),
        product: plansProduct,
      });
      if (planArchivedFilter !== 'all') params.set('archived', String(planArchivedFilter === 'archived'));

      const json = await HttpClient.get(
        `/algolia/search/backoffice/plans?${params.toString()}`,
        authContext.authentication?.token
      );
      setPlans(json.hits);
      setNbHits(json.nbHits);
      setPage(pageToFetch);
    } catch (err) {
      console.error('[Algolia] erreur :', err);
    } finally {
      setIsLoading(false);
    }
  }

  const getStripeProducts = async (productType: EProductType) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ product_status: stripeProductFilter });
      const json = await HttpClient.get(
        `/${productType}/stripe/backoffice/products-with-plans?${params.toString()}`,
        authContext.authentication?.token
      );
      if (json?.success && json.data) {
        setStripeProducts(json.data as IStripeProducts);
      } else {
        setStripeProducts(null);
      }
    } catch (err) {
      console.error('[Stripe Leedflow catalog] erreur :', err);
      setStripeProducts(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (plansProduct !== EProductType.LEEDPULSE) return;
    const timer = setTimeout(() => {
      searchPlans(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, plansProduct, planArchivedFilter]);

  useEffect(() => {
    // We do not link other product than leedflow for now
    if (plansProduct !== EProductType.LEEDFLOW) return;
    // Vrai effet de bord : getStripeProducts appelle l'API Stripe. La partie
    // derivable (le reset a null) est deja sortie dans la garde ci-dessous.
    // eslint-disable-next-line react/set-state-in-effect
    getStripeProducts(plansProduct);
  }, [plansProduct, stripeProductFilter]);

  // Le `setStripeProducts(null)` de l'effet ci-dessus se derive du produit
  // selectionne (pattern « ajuster l'etat quand une prop change » de la doc
  // React) ; seul l'appel reseau reste un effet.
  const [lastPlansProduct, setLastPlansProduct] = useState(plansProduct);
  if (plansProduct !== lastPlansProduct) {
    setLastPlansProduct(plansProduct);
    if (plansProduct !== EProductType.LEEDFLOW) setStripeProducts(null);
  }

  useEffect(() => {
    if (didResolveInitialProductRef.current) return;

    const hasProductInUrl = productQueryParam != null;
    if (hasProductInUrl) {
      isProductInitializedRef.current = true;
      didResolveInitialProductRef.current = true;
      return;
    }

    let savedProduct: EProductType | null = null;
    try {
      savedProduct = parsePlansProductFromLocalStorage(
        window.localStorage.getItem(PLANS_PAGE_PRODUCT_LOCALSTORAGE_KEY)
      );
    } catch {
      savedProduct = null;
    }

    const initialProduct = savedProduct ?? EProductType.LEEDFLOW;
    pendingInitialProductRef.current = initialProduct;
    setProductTab(initialProduct);

    if (initialProduct === EProductType.LEEDPULSE) {
      isProductInitializedRef.current = true;
      pendingInitialProductRef.current = null;
    }

    didResolveInitialProductRef.current = true;
  }, [productQueryParam]);

  useEffect(() => {
    if (isProductInitializedRef.current) return;
    if (pendingInitialProductRef.current !== EProductType.LEEDFLOW) return;
    if (productQueryParam === EProductType.LEEDFLOW) {
      isProductInitializedRef.current = true;
      pendingInitialProductRef.current = null;
    }
  }, [productQueryParam]);

  useEffect(() => {
    if (!isProductInitializedRef.current) return;
    try {
      window.localStorage.setItem(PLANS_PAGE_PRODUCT_LOCALSTORAGE_KEY, plansProduct);
    } catch {
      // ignore
    }
  }, [plansProduct]);

  const filteredStripeProducts = useMemo(() => {
    if (stripeProducts == null || planArchivedFilter === 'all') return stripeProducts;

    const keepArchived = planArchivedFilter === 'archived';
    const products = stripeProducts.products
      .map((product) => {
        const prices = product.prices.map((price) => ({
          ...price,
          plans: price.plans.filter((plan) => Boolean(plan.archived) === keepArchived),
        }));
        return { ...product, prices, plans_count: prices.reduce((s, p) => s + p.plans.length, 0) };
      })
      .filter((product) => product.plans_count > 0);

    return { ...stripeProducts, products };
  }, [stripeProducts, planArchivedFilter]);

  const orphanPlansCount = filteredStripeProducts?.orphan_plans.length ?? 0;

  const leedflowPlanTotal =
    filteredStripeProducts != null
      ? filteredStripeProducts.products.reduce((s, p) => s + p.plans_count, 0) + filteredStripeProducts.orphan_plans.length
      : 0;

  const listTitleCount = plansProduct === EProductType.LEEDFLOW ? leedflowPlanTotal : nbHits;

  return (
    <PageScaffold
      title={PLANS_PAGE_TITLE + ' (' + formatNumber(listTitleCount) + ')'} // total adapter le count quand lf plan change (le filtre) 
      subtitle={"Gérez les plans"}
      breadcrumbItems={[{ title: "Plans" }]}
      controlsSettings={{
        hideSearch: plansProduct === EProductType.LEEDFLOW,
        searchLabel: "Nom du plan",
        onSearch: (value) => setQuery(value ?? ''),
        isLoading: false,
        onSearchCancel: () => setQuery(''),
        actionButtons: !isReadOnly
          ? [
            {
              label: "Nouveau plan",
              type: "primary",
              props: {
                icon: <PlusOutlined />,
                onClick: (e) => {
                  e.preventDefault(); // prevent to navigate when 3rd mouse click (if you want to allow cmd/ctrl + click you'll need some key check instead)
                  navigate("new");
                },
                href: "/plans/new", // open a new tab on 3rd mouse click
                target: "_blank",
                rel: "noopener noreferrer",
              },
            },
            {
              label: 'Rafraîchir',
              type: "primary",
              props: {
                icon: <ReloadOutlined />,
                onClick: () =>
                  plansProduct === EProductType.LEEDFLOW ? getStripeProducts(plansProduct) : searchPlans(page),
              },
            },
          ]
          : [
            {
              label: <ReloadOutlined />,
              type: "primary",
              props: {
                onClick: () =>
                  plansProduct === EProductType.LEEDFLOW ? getStripeProducts(plansProduct) : searchPlans(page),
              },
            },
          ],
      }}
    >
      <Radio.Group style={{ margin: '0 0 1rem 0.5rem' }} value={plansProduct}>
        <Radio.Button value={EProductType.LEEDPULSE} onClick={() => setProductTab(EProductType.LEEDPULSE)}>
          <img src={leedpulseLogo} alt="Leedpulse" style={{ height: '18px' }} />
        </Radio.Button>
        <Radio.Button value={EProductType.LEEDFLOW} onClick={() => setProductTab(EProductType.LEEDFLOW)}>
          <img src={leedflowLogo} alt="Leedflow" style={{ height: '18px' }} />
        </Radio.Button>
      </Radio.Group>
      <Flex gap={24} wrap="wrap" align="flex-end" style={{ margin: '0 0 1rem 0.5rem' }}>
        {plansProduct === EProductType.LEEDFLOW ? (
          <Flex vertical gap={4}>
            <Typography.Text type="secondary">Produit Stripe</Typography.Text>
            <Segmented<TStripeProductStatusFilter>
              value={stripeProductFilter}
              onChange={(v) => setStripeStatusInUrl(v)}
              options={[
                { label: 'Tout', value: 'all' },
                { label: 'Actifs', value: 'active' },
                { label: 'Archivés', value: 'archived' },
              ]}
            />
          </Flex>
        ) : null}
        <Flex vertical gap={4}>
          <Typography.Text type="secondary">Plan</Typography.Text>
          <Segmented<TPlanArchivedFilter>
            value={planArchivedFilter}
            onChange={(v) => setArchivedFilterInUrl(v)}
            options={[
              { label: 'Tout', value: 'all' },
              { label: 'Non archivés', value: 'not_archived' },
              { label: 'Archivés', value: 'archived' },
            ]}
          />
        </Flex>
        {plansProduct === EProductType.LEEDFLOW && isOrphanAlertClosed && orphanPlansCount > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={`${orphanPlansCount} plan${orphanPlansCount > 1 ? 's' : ''} orphelin${orphanPlansCount > 1 ? 's' : ''}`}
            action={
              <Button size="small" type="link" style={{ color: 'var(--warning-color)' }} onClick={() => closeOrphanAlert(false)}>
                Afficher
              </Button>
            }
          />
        ) : null}
      </Flex>
      {plansProduct === EProductType.LEEDFLOW ? (
        <LeedflowStripeProductsList
          stripeProducts={filteredStripeProducts}
          loading={isLoading}
          expandAllRows={planArchivedFilter !== 'not_archived'}
          orphanAlertClosed={isOrphanAlertClosed}
          onOrphanAlertClose={() => closeOrphanAlert(true)}
        />
      ) : (
        <PlansList
          plans={plans}
          loading={isLoading}
          page={page}
          nbHits={nbHits}
          hitsPerPage={hitsPerPage}
          onPageChange={(p) => searchPlans(p)}
          onPageSizeChange={(newPageSize) => {
            setHitsPerPage(newPageSize);
            searchPlans(0, newPageSize);
          }}
        />
      )}
    </PageScaffold>
  );
};

export default PlansPage;
